import {
  ArrowForward as ArrowForwardIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  LocalShipping as LocalShippingIcon,
  Note as NoteIcon,
  Sort as SortIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { OrderStatus } from "../../../types/admin";

interface OrderStatusDetailModalProps {
  open: boolean;
  status: OrderStatus | null;
  allStatuses?: OrderStatus[];
  onClose: () => void;
  onEdit: (status: OrderStatus) => void;
}

const OrderStatusDetailModal: React.FC<OrderStatusDetailModalProps> = ({
  open,
  status,
  allStatuses = [],
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation();
  if (!status) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (color: string) => {
    return color.startsWith("#") ? color : `#${color}`;
  };

  const getTransitionStatuses = () => {
    return allStatuses.filter((s) => status.canTransitionTo.includes(s.id));
  };

  const getStatusIcon = (iconName?: string) => {
    // You can expand this to include more icons
    const iconMap: { [key: string]: React.ReactNode } = {
      pending: <CheckCircleIcon />,
      processing: <SortIcon />,
      shipped: <LocalShippingIcon />,
      delivered: <CheckCircleIcon />,
      cancelled: <CancelIcon />,
      hold: <CancelIcon />,
    };
    return iconMap[iconName || ""] || <CheckCircleIcon />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: getStatusColor(status.color),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              {getStatusIcon(status.icon)}
            </Box>
            <Box>
              <Typography variant="h6">{status.displayName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {status.name}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title={t("admin.orderStatus.editStatus")}>
              <IconButton onClick={() => onEdit(status)} size="small">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("admin.orderStatus.basicInformation")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.name")}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {status.name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.displayName")}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {status.displayName}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.description")}
                  </Typography>
                  <Typography variant="body1">
                    {status.description || t("admin.orderStatus.noDescription")}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Appearance */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("admin.orderStatus.appearance")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.color")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        backgroundColor: getStatusColor(status.color),
                        border: "1px solid #ccc",
                      }}
                    />
                    <Typography variant="body1" fontFamily="monospace">
                      {status.color}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.icon")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    {getStatusIcon(status.icon)}
                    <Typography variant="body1">
                      {status.icon || t("admin.orderStatus.noIcon")}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Configuration */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("admin.orderStatus.configuration")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.sortOrder")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <SortIcon fontSize="small" />
                    <Typography variant="body1" fontWeight="medium">
                      {status.sortOrder}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.status")}
                  </Typography>
                  <Box mt={1}>
                    <Chip
                      label={status.isActive ? t("admin.orderStatus.active") : t("admin.orderStatus.inactive")}
                      color={status.isActive ? "success" : "error"}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {t("admin.orderStatus.requirements")}
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {status.requiresTrackingNumber && (
                      <Chip
                        icon={<LocalShippingIcon />}
                        label={t("admin.orderStatus.requiresTrackingNumber")}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {status.requiresNote && (
                      <Chip
                        icon={<NoteIcon />}
                        label={t("admin.orderStatus.requiresNote")}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {!status.requiresTrackingNumber && !status.requiresNote && (
                      <Typography variant="body2" color="text.secondary">
                        {t("admin.orderStatus.noSpecialRequirements")}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Transitions */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("admin.orderStatus.allowedTransitions")}
              </Typography>
              {status.canTransitionTo.length > 0 ? (
                <List dense>
                  {getTransitionStatuses().map((transitionStatus) => (
                    <ListItem key={transitionStatus.id} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <ArrowForwardIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={transitionStatus.displayName}
                        secondary={transitionStatus.name}
                      />
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: "50%",
                          backgroundColor: getStatusColor(
                            transitionStatus.color,
                          ),
                          ml: 1,
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("admin.orderStatus.noTransitionsAllowed")}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Usage Statistics */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("admin.orderStatus.usageStatistics")}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.ordersUsingThisStatus")}
                  </Typography>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {status.orderCount}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.created")}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(status.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.orderStatus.lastUpdated")}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(status.updatedAt)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t("admin.orderStatus.close")}</Button>
        <Button
          onClick={() => onEdit(status)}
          variant="contained"
          startIcon={<EditIcon />}
        >
          {t("admin.orderStatus.editStatus")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrderStatusDetailModal;
